import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { formatDate, formatMoney, formatNumber } from '@/lib/format'
import { cardHref } from '@/lists/cellValue'
import { AttachmentsPanel } from './AttachmentsPanel'
import {
  cancelDocument,
  fetchDocument,
  fetchReissues,
  fetchSignature,
  issueDocument,
  lineArithmetic,
  linesReconcile,
  markSentByEdi,
  reissueDocument,
  signDocument,
  type PrimaryDocument,
  type SignatureCheck,
} from './documentApi'
import { HistoryPanel } from './HistoryPanel'
import {
  ConfirmModal,
  Facts,
  Panel,
  RecordHeader,
  RecordTabs,
  StageBar,
  StateView,
  type Fact,
  type HeaderAction,
  type MenuAction,
  type StageStep,
} from './record'
import { listPath } from './recordHelpers'
import {
  FailureAlert,
  FormModal,
  RecordFailure,
  RecordSkeleton,
  RequiredText,
  StatusBadge,
  copyLinkItem,
  failureText,
  labelOf,
} from './recordKit'
import type { RecordViewProps } from './types'

/**
 * Карточка первичного документа (PrimaryDoc.dc.html, тикет #73).
 *
 * Статус подписи — отдельной плашкой `.sig` над вкладками, и состояний у неё три, а не два:
 * «подписан и сходится», «подписан, но содержимое изменено после подписи» и «не подписан».
 * «Не подписан» и «подпись не сходится» — **разные факты**: `matches` у неподписанного
 * всегда `true` («противоречить нечему»), поэтому сначала смотрим на `signed`. Слитые в
 * один флаг, они выставили бы каждый черновик подделанным.
 *
 * Основное действие — по статусу: черновик выставляют, выставленный подписывают (право
 * `approve`), документ с разошедшейся подписью перевыпускают — с обязательной причиной:
 * перевыпуск без неё неотличим от постороннего документа с теми же строками.
 * Аннулирование — в «⋯» за чертой с подтверждением; подписанный документ домен не
 * аннулирует (его перевыпускают), и пункта у него нет.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - **События ЭДО** (доставлен, просмотрен, подписан покупателем), **подпись покупателя**,
 *   **сертификаты**, **«Напомнить покупателю»** — у документа одна подпись (`signedAt`,
 *   `signedBy`) и отметка отправки (`ediMessageId`); оператора ЭДО нет (заглушка #56).
 * - **Какая строка изменена после подписи и «было → стало»** — сервер хранит хеш
 *   подписанного содержимого, а не его копию; показать можно только факт расхождения.
 * - **PDF документа** — побайтового рендера нет (CLAUDE.md, #15).
 * - **«Заполнить из отгрузки»** — строки приходят из отгрузки при выпуске, операции
 *   заполнения нет.
 */
export function DocumentRecordView({ recordId, record, context, notices }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('lines')
  const [failure, setFailure] = useState<string | null>(null)
  const [reissuing, setReissuing] = useState(false)
  const [reason, setReason] = useState('')
  const [edi, setEdi] = useState(false)
  const [messageId, setMessageId] = useState('')
  const [confirmCancel, setConfirmCancel] = useState(false)

  const documentQuery = useQuery({ queryKey: ['document', recordId], queryFn: ({ signal }) => fetchDocument(recordId, signal), retry: false })
  const signature = useQuery({
    queryKey: ['document', recordId, 'signature'],
    queryFn: ({ signal }) => fetchSignature(recordId, signal),
    retry: false,
  })
  const reissues = useQuery({
    queryKey: ['document', recordId, 'reissues'],
    queryFn: ({ signal }) => fetchReissues(recordId, signal),
    retry: false,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['document', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['record', 'billing.document', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['list', 'billing.document'] })
  }
  const succeed = () => {
    setFailure(null)
    refresh()
  }
  const fail = (error: unknown) => setFailure(failureText(error, t('app.error')))

  const issue = useMutation({ mutationFn: () => issueDocument(recordId), onSuccess: succeed, onError: fail })
  const sign = useMutation({ mutationFn: () => signDocument(recordId), onSuccess: succeed, onError: fail })
  const markEdi = useMutation({
    mutationFn: () => markSentByEdi(recordId, messageId.trim()),
    onSuccess: () => {
      setEdi(false)
      setMessageId('')
      succeed()
    },
    onError: fail,
  })
  const cancel = useMutation({
    mutationFn: () => cancelDocument(recordId),
    onSuccess: () => {
      setConfirmCancel(false)
      succeed()
    },
    onError: (error) => {
      setConfirmCancel(false)
      fail(error)
    },
  })
  const reissue = useMutation({
    mutationFn: () => reissueDocument(recordId, reason.trim()),
    onSuccess: () => {
      setReissuing(false)
      setReason('')
      succeed()
    },
    onError: fail,
  })

  if (documentQuery.isLoading) return <RecordSkeleton />
  if (!documentQuery.data) {
    return (
      <RecordFailure error={documentQuery.error} onRetry={() => void documentQuery.refetch()} listPath={listPath('billing.document', '/documents')} />
    )
  }
  const doc = documentQuery.data
  const check = signature.data
  const mismatch = Boolean(check?.signed && !check.matches)
  const mayUpdate = can(user, 'billing.document', 'update')
  const mayApprove = can(user, 'billing.document', 'approve')
  const mayCreate = can(user, 'billing.document', 'create')
  const busy = issue.isPending || sign.isPending || markEdi.isPending || cancel.isPending || reissue.isPending
  const money = (value: number) => formatMoney(value, doc.currency, locale)
  const mayReissue = mayCreate && doc.status !== 'draft' && doc.status !== 'cancelled'

  // ------------------------------------------------------------------ шапка

  let primary: HeaderAction | null = null
  const secondary: HeaderAction[] = []
  if (mismatch && mayReissue) {
    primary = { id: 'reissue', label: t('record.doc.reissueContinue'), icon: 'refresh', onClick: () => setReissuing(true), disabled: busy }
  } else if (doc.status === 'draft' && mayUpdate) {
    primary = { id: 'issue', label: t('card.doc.action.issue'), icon: 'check', onClick: () => issue.mutate(), disabled: busy }
  } else if (doc.status === 'issued' && mayApprove) {
    primary = { id: 'sign', label: t('card.doc.action.sign'), icon: 'sign', onClick: () => sign.mutate(), disabled: busy }
  }
  if (mayUpdate && (doc.status === 'issued' || doc.status === 'signed') && !doc.ediMessageId) {
    secondary.push({ id: 'edi', label: t('record.doc.ediContinue'), icon: 'send', onClick: () => setEdi(true), disabled: busy })
  }

  const menu: MenuAction[] = [copyLinkItem(t, setFailure)]
  if (mayReissue && !mismatch) {
    menu.push({ id: 'reissue', label: t('record.doc.reissueContinue'), icon: 'refresh', onSelect: () => setReissuing(true) })
  }
  if (mayUpdate && doc.status !== 'cancelled' && doc.status !== 'signed') {
    menu.push({ id: 'cancel', label: t('record.doc.cancelContinue'), icon: 'close', danger: true, onSelect: () => setConfirmCancel(true) })
  }

  const counterpartyName = labelOf(record.values, 'counterparty_id')
  const organizationName = labelOf(record.values, 'organization_id')
  const invoiceNumber = labelOf(record.values, 'invoice_id')
  const contractNumber = labelOf(record.values, 'contract_id')
  const link = (entity: string, id: string | null, label: string | null, mono = false) => {
    if (!id) return null
    const href = cardHref(user, entity, id)
    const text = label ?? id.slice(0, 8)
    return href ? <Link to={href} className={mono ? 'mono' : undefined}>{text}</Link> : <span className={mono ? 'mono' : undefined}>{text}</span>
  }
  const meta = [
    <StatusBadge key="status" entity="billing.document" value={doc.status} />,
    organizationName ? <span key="seller">{t('record.doc.seller')} {organizationName}</span> : null,
    <span key="buyer">
      {t('record.doc.buyer')} {link('sales.counterparty', doc.counterpartyId, counterpartyName) ?? '—'}
    </span>,
    doc.invoiceId ? (
      <span key="invoice">
        {t('record.doc.toInvoice')} {link('billing.invoice', doc.invoiceId, invoiceNumber, true)}
      </span>
    ) : null,
    doc.contractId ? (
      <span key="contract">
        {t('record.doc.contract')} {link('billing.contract', doc.contractId, contractNumber)}
      </span>
    ) : null,
  ]

  // ------------------------------------------------------------------ этапы, факты

  const order = ['draft', 'issued', 'edi', 'signed']
  const reached = doc.status === 'signed' ? 3 : doc.ediMessageId ? 2 : doc.status === 'issued' ? 1 : 0
  const steps: StageStep[] = order.map((code, index) => ({
    id: code,
    label: t(`record.doc.stage.${code}`),
    state: doc.status === 'cancelled' ? (index === 0 ? 'done' : 'future') : index < reached ? 'done' : index === reached ? 'current' : 'future',
    warning: code === 'signed' && mismatch,
    meta:
      code === 'draft'
        ? formatDate(doc.documentDate, locale)
        : code === 'edi' && doc.ediSentAt
          ? formatDate(doc.ediSentAt.slice(0, 10), locale)
          : code === 'signed' && doc.signedAt
            ? mismatch
              ? t('record.doc.stage.mismatch')
              : formatDate(doc.signedAt.slice(0, 10), locale)
            : undefined,
  }))

  const lines = doc.lines ?? []
  const facts: Fact[] = [
    { id: 'total', label: t('record.doc.fact.total'), value: money(doc.total), sub: t('record.doc.fact.lines', { count: lines.length }) },
    { id: 'vat', label: t('record.doc.fact.vat'), value: money(doc.vatTotal), sub: t('record.doc.fact.net', { amount: money(doc.subtotal) }) },
    { id: 'date', label: t('record.doc.fact.date'), value: formatDate(doc.documentDate, locale) },
    {
      id: 'basis',
      label: t('record.doc.fact.basis'),
      value: (
        <span className="t-md" style={{ fontWeight: 500 }}>
          {doc.contractId ? link('billing.contract', doc.contractId, contractNumber) : doc.invoiceId ? link('billing.invoice', doc.invoiceId, invoiceNumber, true) : t('record.notSet')}
        </span>
      ),
    },
  ]

  // ------------------------------------------------------------------ вкладки

  const drifting = lines.filter((line) => lineArithmetic(line).total.toFixed(2) !== line.lineTotal.toFixed(2))
  const linesPanel = (
    <Panel title={t('record.doc.lines')} count={lines.length} flush actions={<span className="t-sm muted">{t('record.doc.lines.hint')}</span>}>
      {lines.length === 0 ? (
        <StateView compact kind="empty" title={t('card.doc.lines.empty')} description={t('record.doc.lines.emptyText')} />
      ) : (
        <>
          {!linesReconcile(doc) && (
            <div className="alert alert-danger" role="alert" style={{ margin: 12 }}>
              <Icon name="alert" />
              <span>{t('card.doc.lines.mismatch')}</span>
            </div>
          )}
          {drifting.length > 0 && (
            <div className="alert alert-danger" role="alert" style={{ margin: 12 }}>
              <Icon name="alert" />
              <span>{t('card.doc.lines.lineMismatch', { count: drifting.length })}</span>
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl" aria-label={t('record.doc.lines')}>
              <thead>
                <tr>
                  <th style={{ width: 28 }}>№</th>
                  <th>{t('card.doc.line.description')}</th>
                  <th className="num">{t('card.doc.line.quantity')}</th>
                  <th className="num">{t('card.doc.line.price')}</th>
                  <th className="num">{t('record.doc.col.net')}</th>
                  <th className="num">{t('card.doc.line.vat')}</th>
                  <th className="num">{t('card.doc.line.total')}</th>
                </tr>
              </thead>
              <tbody>
                {[...lines]
                  .sort((a, b) => a.lineOrder - b.lineOrder)
                  .map((line) => (
                    <tr key={line.id}>
                      <td className="muted">{line.lineOrder}</td>
                      <td className="wrap">{line.description}</td>
                      <td className="num">{formatNumber(line.quantity, locale)}</td>
                      <td className="num">{money(line.unitPrice)}</td>
                      <td className="num">{money(Math.round((line.lineTotal - line.vatAmount) * 100) / 100)}</td>
                      <td className="num">
                        <span className="stack" style={{ gap: 0, alignItems: 'flex-end' }}>
                          <span>{money(line.vatAmount)}</span>
                          <span className="t-xs muted">{formatNumber(line.vatRate, locale)} %</span>
                        </span>
                      </td>
                      <td className="num strong">{money(line.lineTotal)}</td>
                    </tr>
                  ))}
                <tr className="total">
                  <td />
                  <td>{t('card.doc.subtotal')}</td>
                  <td />
                  <td />
                  <td className="num">{money(doc.subtotal)}</td>
                  <td className="num">{money(doc.vatTotal)}</td>
                  <td className="num">{money(doc.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {mismatch && (
            <div className="panel-f">
              <span className="flag flag-danger">
                <Icon name="alert" />
                {t('record.doc.lines.notSigned')}
              </span>
            </div>
          )}
        </>
      )}
    </Panel>
  )

  const signaturesTab = (
    <div className="layout-2-wide" style={{ paddingTop: 14 }}>
      <div className="rec-col">
        <Panel title={t('record.doc.signature')}>
          <dl className="kv">
            <dt>{t('card.doc.signedAt')}</dt>
            <dd>{doc.signedAt ? new Date(doc.signedAt).toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU') : '—'}</dd>
            <dt>{t('card.doc.edi')}</dt>
            <dd className="mono">{doc.ediMessageId ?? t('card.doc.edi.none')}</dd>
            {check?.signed && (
              <>
                <dt>{t('card.doc.sign.signedHash')}</dt>
                <dd className="mono t-xs ellipsis">{check.signedHash}</dd>
                <dt>{t('card.doc.sign.currentHash')}</dt>
                <dd className="mono t-xs ellipsis">{check.currentHash}</dd>
              </>
            )}
            {doc.reissuedFromId && (
              <>
                <dt>{t('card.doc.reissuedFrom')}</dt>
                <dd>
                  {doc.reissueReason ?? '—'} ·{' '}
                  <Link to={`${listPath('billing.document', '/documents')}/${doc.reissuedFromId}`}>{t('record.doc.original')}</Link>
                </dd>
              </>
            )}
          </dl>
        </Panel>
        <Panel title={t('card.tab.attachments')}>
          <AttachmentsPanel entityType={context.entity} entityId={recordId} resource={context.resource} />
        </Panel>
      </div>
      <div className="rec-col">
        <Panel title={t('card.doc.reissues')} count={reissues.data?.length ?? null}>
          <ReissueChain query={reissues} />
        </Panel>
      </div>
    </div>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.documents'), to: listPath('billing.document', '/documents') }, { label: doc.number, mono: true }]}
        title={
          <>
            {doc.kindLabel || t(`value.billing.document.document_kind.${doc.kind}`)} <span className="rec-title-mono">{doc.number}</span>{' '}
            <span className="rec-title-date">{t('record.doc.from', { date: formatDate(doc.documentDate, locale) })}</span>
          </>
        }
        meta={meta}
        primary={primary}
        secondary={secondary}
        menu={menu}
        menuLabel={t('record.more')}
      />
      <FailureAlert text={failure} />
      {notices}
      {doc.status === 'cancelled' && (
        <div className="alert alert-neutral" role="status">
          <Icon name="close" />
          <span>{t('record.doc.cancelled')}</span>
        </div>
      )}

      <StageBar steps={steps} ariaLabel={t('record.doc.stages')} />
      <Facts items={facts} ariaLabel={t('record.facts')} />
      <SignatureBlock check={check} loading={signature.isLoading} error={signature.error} doc={doc} />

      <RecordTabs
        tabs={[
          { id: 'lines', label: t('record.doc.tab.lines'), count: lines.length },
          { id: 'signatures', label: t('record.doc.tab.signatures') },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.doc.sections')}
      >
        {tab === 'lines' && <div className="rec-col" style={{ paddingTop: 14 }}>{linesPanel}</div>}
        {tab === 'signatures' && signaturesTab}
        {tab === 'history' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('record.history.changes')}>
              <HistoryPanel entity={context.entity} recordId={recordId} />
            </Panel>
          </div>
        )}
      </RecordTabs>

      <FormModal
        open={reissuing}
        overline={t('record.doc.reissue.overline')}
        icon="refresh"
        title={t('record.doc.reissue.title', { number: doc.number })}
        confirmLabel={t('card.doc.action.reissue')}
        canConfirm={reason.trim() !== ''}
        missingHint={t('record.doc.reissue.missing')}
        pending={reissue.isPending}
        onCancel={() => setReissuing(false)}
        onConfirm={() => reissue.mutate()}
      >
        <div className="alert alert-neutral">
          <span className="fg">{t('record.doc.reissue.text', { number: doc.number, date: formatDate(doc.documentDate, locale) })}</span>
        </div>
        <RequiredText
          label={t('card.doc.reissue.reason')}
          value={reason}
          onChange={setReason}
          placeholder={t('record.doc.reissue.placeholder')}
          help={t('record.doc.reissue.help')}
        />
      </FormModal>

      <FormModal
        open={edi}
        icon="send"
        title={t('record.doc.edi.title', { number: doc.number })}
        confirmLabel={t('card.doc.action.edi')}
        canConfirm={messageId.trim() !== ''}
        missingHint={t('record.doc.edi.missing')}
        pending={markEdi.isPending}
        onCancel={() => setEdi(false)}
        onConfirm={() => markEdi.mutate()}
      >
        <RequiredText
          multiline={false}
          label={t('card.doc.edi.messageId')}
          value={messageId}
          onChange={setMessageId}
          help={t('record.doc.edi.help')}
        />
      </FormModal>

      <ConfirmModal
        open={confirmCancel}
        icon="close"
        title={t('record.doc.cancel.title', { number: doc.number })}
        confirmLabel={t('card.doc.action.cancel')}
        pending={cancel.isPending}
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() => cancel.mutate()}
      >
        <p>{t('record.doc.cancel.text')}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.irreversible')}
        </p>
      </ConfirmModal>
    </div>
  )
}

/**
 * Плашка подписи `.sig`: три состояния, а не два. Порядок проверок важен — сначала
 * «подписан ли», потом «сходится ли»: у неподписанного `matches === true`.
 */
export function SignatureBlock({
  check,
  loading,
  error,
  doc,
}: {
  check: SignatureCheck | undefined
  loading: boolean
  error: unknown
  doc: PrimaryDocument
}) {
  const { t, locale } = useApp()
  if (loading) return null
  if (!check) {
    return (
      <StateView
        compact
        kind={error instanceof ApiError && error.isForbidden ? 'forbidden' : 'error'}
        title={t('card.doc.sign.unknown')}
      />
    )
  }
  const at = doc.signedAt ? new Date(doc.signedAt).toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU') : ''
  if (!check.signed) {
    return (
      <div className="sig is-none" role="status">
        <span className="ico">
          <Icon name="sign" />
        </span>
        <div className="stack" style={{ gap: 0 }}>
          <span className="strong">{t('card.doc.sign.unsigned')}</span>
          <span className="t-sm secondary">
            {doc.status === 'draft' ? t('record.doc.sig.draft') : t('record.doc.sig.unsignedText')}
          </span>
        </div>
        <span />
      </div>
    )
  }
  if (!check.matches) {
    return (
      <div className="sig is-bad" role="alert">
        <span className="ico">
          <Icon name="alert" />
        </span>
        <div className="stack" style={{ gap: 0 }}>
          <span className="strong" style={{ color: 'var(--danger-text)' }}>
            {t('card.doc.sign.mismatch')}
          </span>
          <span className="t-sm secondary">{t('card.doc.sign.mismatchExplained')}</span>
        </div>
        <span />
      </div>
    )
  }
  return (
    <div className="sig is-ok" role="status">
      <span className="ico">
        <Icon name="check" />
      </span>
      <div className="stack" style={{ gap: 0 }}>
        <span className="strong" style={{ color: 'var(--success-text)' }}>
          {t('card.doc.sign.intact')}
        </span>
        <span className="t-sm secondary">{t('record.doc.sig.okText', { at })}</span>
      </div>
      <span />
    </div>
  )
}

function ReissueChain({
  query,
}: {
  query: { isLoading: boolean; isError: boolean; error: unknown; data: PrimaryDocument[] | undefined }
}) {
  const { t, locale } = useApp()
  if (query.isLoading) return <p className="muted">{t('app.loading')}</p>
  if (query.isError) {
    return (
      <StateView
        compact
        kind={query.error instanceof ApiError && query.error.isForbidden ? 'forbidden' : 'error'}
        title={t('record.doc.reissues.error')}
      />
    )
  }
  const chain = query.data ?? []
  if (chain.length === 0) return <p className="secondary">{t('card.doc.reissues.empty')}</p>
  return (
    <table className="tbl" aria-label={t('card.doc.reissues')}>
      <thead>
        <tr>
          <th>{t('card.doc.number')}</th>
          <th>{t('card.doc.date')}</th>
          <th>{t('card.doc.reissue.reason')}</th>
          <th>{t('card.doc.status')}</th>
        </tr>
      </thead>
      <tbody>
        {chain.map((replacement) => (
          <tr key={replacement.id}>
            <td>
              <Link to={`${listPath('billing.document', '/documents')}/${replacement.id}`} className="mono">
                {replacement.number}
              </Link>
            </td>
            <td>{formatDate(replacement.documentDate, locale)}</td>
            <td className="wrap">{replacement.reissueReason ?? '—'}</td>
            <td>
              <StatusBadge entity="billing.document" value={replacement.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
